import React, { useState } from 'react'
import {
  EntryType,
  QuestionSettingCode,
  QuestionTypeCode
} from '../../../data/model/questionnaire'
import { useCreateQuestionnaireEntryV2 } from '../../../hooks/questionnaire/useCreateQuestionnaireEntryV2'
import useGetDraftQuestionnaire from '../../../hooks/questionnaire/useGetDraftQuestionnaire'
import { addQuestionTransactionDatadog } from '../../../tracking/perf/transactions'
import { getForkCount } from '../../../utils/questionnaireUtils'
import { ImportQuestions } from '../ImportQuestions'
import { colours as TextHighlighterColours } from '../TextHighlighter/TextHighlighterColourSelect'
import AddQuestionControl from './AddQuestionMenu.control'
import {
  AddQuestionOption,
  AddQuestionSecondaryOption
} from './AddQuestionMenu.model'

enum DEFAULT_OPTIONS_COUNT {
  ZERO = 0,
  AT_LEAST_TWO = 2
}

interface Props {
  showAddButton?: boolean
  showSectionMenu?: boolean
  position: number
  sectionId?: string
}

const AddQuestionMenu: React.FC<Props> = ({
  position,
  showAddButton = false,
  showSectionMenu = false,
  sectionId
}: Props) => {
  const { draftQuestionnaire, refetchQuestionnaire } =
    useGetDraftQuestionnaire()
  const createQuestionnaireEntryV2 = useCreateQuestionnaireEntryV2()
  const [importQuestionsDialogOpen, setImportQuestionsDialogOpen] =
    useState<boolean>(false)

  const handleCreateNewQuestion: (
    addQuestionOption: AddQuestionOption
  ) => void = async (addQuestionOption) => {
    const defaultOptionsCount =
      addQuestionOption.questionTypeCode === QuestionTypeCode.FreeText
        ? DEFAULT_OPTIONS_COUNT.ZERO
        : DEFAULT_OPTIONS_COUNT.AT_LEAST_TWO
    addQuestionTransactionDatadog.start()
    switch (addQuestionOption.questionTypeCode) {
      case QuestionTypeCode.Basic:
      case QuestionTypeCode.Ranked:
      case QuestionTypeCode.MaxDiff:
      case QuestionTypeCode.FreeText: {
        createQuestionnaireEntryV2({
          sectionId,
          position,
          createQuestionItem: {
            text: '',
            questionTypeCode: addQuestionOption.questionTypeCode,
            defaultResponseOptionsCount: defaultOptionsCount,
            settingValues:
              addQuestionOption.settings?.map((setting) => ({
                questionSettingCode: setting.questionSettingCode,
                settingValue: setting.settingValue
              })) || []
          }
        })
        //TODO: Do we still need the optimistic response?
        break
      }
      case QuestionTypeCode.Scale: {
        createQuestionnaireEntryV2({
          sectionId,
          position,
          createQuestionItem: {
            text: '',
            questionTypeCode: QuestionTypeCode.Scale,
            scaleRange: {
              min: 0,
              max: 100,
              step: 1,
              startingPosition: 0
            },
            settingValues:
              addQuestionOption.settings?.map((setting) => ({
                questionSettingCode: setting.questionSettingCode,
                settingValue: setting.settingValue
              })) || []
          }
        })
        break
      }
      case QuestionTypeCode.Matrix: {
        createQuestionnaireEntryV2({
          sectionId,
          position,
          createMatrixItem: {
            title: '',
            settingValues:
              addQuestionOption.settings?.map((setting) => ({
                questionSettingCode: setting.questionSettingCode,
                settingValue: setting.settingValue
              })) || [],
            defaultMatrixRowsCount: defaultOptionsCount,
            defaultResponseOptionsCount: defaultOptionsCount
          }
        })

        break
      }
      case QuestionTypeCode.TextHighlighter: {
        createQuestionnaireEntryV2({
          sectionId,
          position,
          createTextHighlighter: {
            questionText: '',
            contentText: '',
            // Default to 2 empty categories
            categories: [0, 1].map((i) => ({
              position: i,
              responseOptionValue: '',
              colour: {
                hex: TextHighlighterColours[i].value,
                label: TextHighlighterColours[i].name
              }
            }))
          }
        })

        break
      }
      case QuestionTypeCode.Conjoint: {
        createQuestionnaireEntryV2({
          sectionId,
          position,
          createConjointItem: {
            text: '',
            attributes: [0, 1].map((i) => ({
              name: '',
              position: i,
              options: [0, 1].map((j) => ({
                value: '',
                position: j
              }))
            })),
            settingValues: [
              {
                questionSettingCode: QuestionSettingCode.ConjointPanelSize,
                settingValue: '2'
              },
              {
                questionSettingCode: QuestionSettingCode.ConjointNumberOfPanels,
                settingValue: '5'
              }
            ]
          }
        })
        break
      }
      default:
    }
  }

  const handleCreateSecondaryCard: (
    addQuestionSecondaryOption: AddQuestionSecondaryOption
  ) => void = async (addQuestionSecondaryOption) => {
    switch (addQuestionSecondaryOption.entryType) {
      case EntryType.ForkEntryType:
        createQuestionnaireEntryV2({
          position,
          createForkItem: {
            name: `Fork ${getForkCount(draftQuestionnaire?.entries)}`,
            defaultBranchNumber: 2
          }
        })
        break
      case EntryType.TextCardEntryType:
        createQuestionnaireEntryV2({
          sectionId,
          position,
          createTextCardItem: {
            title: ''
          }
        })

        break
      case EntryType.SectionEntryType: {
        createQuestionnaireEntryV2({
          position,
          createSectionItem: {
            title: 'Section'
          }
        })
        break
      }
      default:
    }
  }

  const handleImportFromOtherSurvey = (): void => {
    setImportQuestionsDialogOpen(true)
  }

  return (
    <>
      <AddQuestionControl
        showAddButton={showAddButton}
        showSectionMenu={showSectionMenu}
        onCreateNewQuestion={handleCreateNewQuestion}
        onCreateSecondaryEntry={handleCreateSecondaryCard}
        onImportFromOtherSurveyClick={handleImportFromOtherSurvey}
      />
      {importQuestionsDialogOpen && (
        <ImportQuestions
          position={position}
          refetchQuestionnaire={refetchQuestionnaire}
          onClose={() => setImportQuestionsDialogOpen(false)}
        />
      )}
    </>
  )
}

export default React.memo(AddQuestionMenu)
